import React, { useMemo, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Table, Container, Row, Col, Button } from "react-bootstrap";
import TableFilter from "../../../components/TableFilter";
import { useAddNewStore } from "../../../store/add_new_store";
import WarningDialog from "../dialogs/WarningDialog";

const fetchCompanyData = async (token) => {
  const url =
    MOBY_API_URL +
    "/api/usermanager/v1/users/" +
    token?.user?.sub +
    "/companies?txid=" + token.sessionId;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token.token,
    },
  });
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  return response.json();
};

const fetchPanelsList = async (token, companyId) => {
  const response = await fetch(
    MOBY_API_URL + "/api/companymanager/v1/companies/" + companyId + "/devices?txid=" + token.sessionId,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token.token,
      },
    }
  );
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  return response.json();
};

const PanelsList = ({ token }) => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const [openWarning, setWarningOpen] = useState(false);
  const [panelDeselection, setPanelDeselection] = useState("");
  const [openDuplicateWarning, setDuplicateWarningOpen] = useState(false);

  const {
    selectedPanels,
    setSelectedPanels,
    selectedPanelsRow,
    setSelectedPanelsRow,
  } = useAddNewStore();

  const {
    data: companyData,
    isLoading: fetchCompanyLoading,
    isError: fetchCompanyError,
  } = useQuery({
    queryKey: ["companyData"],
    queryFn: () => fetchCompanyData(token),
    enabled: !!token?.user?.sub,
  });

  const onCloseModal = () => {
    setWarningOpen(false);
    table.getRowModel().rows.filter((r) => r.id == panelDeselection)[0].toggleSelected();
    setPanelDeselection("");
  }

  const companyId = useMemo(() => {
    if (companyData?.data?.length > 0) {
      return companyData.data[0]?.companyId || null;
    }
    return null;
  }, [companyData]);

  const {
    data: panelsList,
    isLoading: fetchListLoading,
    isError: fetchListError,
  } = useQuery({
    queryKey: ["panelsList", companyId],
    queryFn: () => fetchPanelsList(token, companyId),
    enabled: !!companyId,
  });

  const columns = useMemo(
    () => [
      { accessorKey: "cloudId", header: "Cloud ID" },
      { accessorKey: "panelName", header: "Panel Name" },
    ],
    []
  );

  const duplicateCloudIds = useMemo(
    () => new Set(panelsList?.data?.duplicateCloudIds || []),
    [panelsList]
  );

  const table = useReactTable({
    data: panelsList?.data?.panels || [],
    columns,
    state: { globalFilter, rowSelection },
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: "auto",
    enableRowSelection: true,
    enableMultiRowSelection: true,
    getRowId: (row) => row.deviceId || row.cloudId || `row-${index}`,
  });

  useEffect(() => {
    const selectedRows = table
      .getSelectedRowModel()
      .rows.map((row) => row.original);

    if (JSON.stringify(selectedRows) !== JSON.stringify(selectedPanels)) {
      setSelectedPanels(selectedRows);
    }

    const newSelectedPanelsRow = Object.fromEntries(
      selectedRows.map((panel) => [panel.deviceId, true])
    );
    if (
      JSON.stringify(newSelectedPanelsRow) !== JSON.stringify(selectedPanelsRow)
    ) {
      setSelectedPanelsRow(newSelectedPanelsRow);
    }
  }, [
    rowSelection,
    setSelectedPanels,
    setSelectedPanelsRow,
    table,
    selectedPanels,
    selectedPanelsRow,
  ]);

  const selectableRows = useMemo(
    () => table.getRowModel().rows.filter((row) => !duplicateCloudIds.has(row.original.cloudId) && !!row.original.deviceId),
    [table, duplicateCloudIds]
  );

  const handleSelectAll = () => {
    if (Object.keys(rowSelection).length === selectableRows.length) {
      setRowSelection({});
      setSelectedPanelsRow({});
    } else {
      const allRowIds = Object.fromEntries(selectableRows.map((row) => [row.id, true]));
      setRowSelection(allRowIds);
      setSelectedPanelsRow(allRowIds);
    }
  };

  if (fetchCompanyLoading) return <div>Loading companies...</div>;
  if (fetchListLoading) return <div>Loading panels...</div>;
  if (fetchCompanyError) return <div>Couldn't find company data</div>;
  if (fetchListError) return <div>Couldn't find panels list</div>;

  return (
    <Container>
      <Row className="mt-4">
        <Col>
          <h4>
            Available Panels for Invite (find and "click" on panel below to add
            to panel invite list above):
          </h4>
        </Col>
      </Row>
      <Row className="mt-2">
        <Col className="d-flex">
          <div className="flex-grow-1 me-3">
            <TableFilter
              filterValue={globalFilter}
              setFilter={setGlobalFilter}
            />
          </div>
          <Button variant="primary" onClick={handleSelectAll}>
            {Object.keys(rowSelection).length === selectableRows.length && selectableRows.length > 0
              ? "Deselect All"
              : "Select All"}
          </Button>
        </Col>
      </Row>
      <Row className="mt-2">
        <Col>
          <Table bordered variant="info">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => {
                const isDuplicate = duplicateCloudIds.has(row?.original?.cloudId);
                return (
                <tr
                  key={row.id}
                  className={isDuplicate ? "panel-row-duplicate" : row.getIsSelected() ? "panel-row-selected" : "panel-row-normal"}
                  onClick={() => {
                    if (isDuplicate) {
                      setDuplicateWarningOpen(true);
                      return;
                    }
                    if (!row?.original?.deviceId) {
                      setWarningOpen(true);
                      setPanelDeselection(row?.original?.cloudId ?? "");
                    }
                    row.toggleSelected();
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        backgroundColor: "inherit",
                        cursor: "pointer"
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              );})}
            </tbody>
          </Table>
        </Col>
      </Row>
      <WarningDialog
        descTranslatedId="panel-set-up-warning"
        onConfirm={onCloseModal}
        onCancel={onCloseModal}
        show={openWarning}
      />
      <WarningDialog
        title="Action Required"
        descTranslatedId="duplicate-cloud-id-warning"
        onConfirm={() => setDuplicateWarningOpen(false)}
        onCancel={() => setDuplicateWarningOpen(false)}
        show={openDuplicateWarning}
      />
    </Container>
  );
};

export default PanelsList;
